import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Wind, Heart, Sparkles } from "lucide-react";
import Footer from "../components/Footer";

export default function Discover() {
  return (
    <div className="min-h-screen bg-[#FDFBF7] texture-overlay">
      {/* Navigation */}
      <nav className="px-6 py-4 border-b border-[#E8E4DE]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-light tracking-wide text-[#2D2A26]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            the soft ritual
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button 
                variant="ghost" 
                className="rounded-full px-6 text-[#6E6A66] hover:text-[#2D2A26] hover:bg-[#E6E2DD]"
              >
                Sign in
              </Button>
            </Link>
            <Link to="/register">
              <Button className="rounded-full px-6 py-2 bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                Begin
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* What is The Soft Ritual Section */}
      <section className="py-12 md:py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 
            className="text-4xl md:text-5xl font-light text-[#2D2A26] text-center mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            What is The Soft Ritual?
          </h1>
          <p className="text-center text-[#6E6A66] font-light mb-10 max-w-xl mx-auto text-lg">
            A gentle AI companion for emotional check-ins and nervous system care.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* What it is */}
            <div className="bg-[#F9F7F4] rounded-2xl p-6 md:p-8">
              <h3 
                className="text-lg font-normal text-[#A87C6D] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                What it is
              </h3>
              <ul className="space-y-2 text-[#6E6A66] font-light text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#7A9E7E] mt-0.5">•</span>
                  <span>A space to pause and check in with yourself</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7A9E7E] mt-0.5">•</span>
                  <span>Short, guided emotional reflections (5-10 min)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7A9E7E] mt-0.5">•</span>
                  <span>Gentle prompts to name what you're feeling</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7A9E7E] mt-0.5">•</span>
                  <span>Simple rituals to settle your nervous system</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7A9E7E] mt-0.5">•</span>
                  <span>A warm, non-judgmental presence</span>
                </li>
              </ul>
            </div>

            {/* What it's not */}
            <div className="bg-[#F5F3EF] rounded-2xl p-6 md:p-8">
              <h3 
                className="text-lg font-normal text-[#6E6A66] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                What it's not
              </h3>
              <ul className="space-y-2 text-[#A8A4A0] font-light text-sm">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>Not therapy or a replacement for professional care</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>Not coaching, advice-giving, or problem-solving</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>Not journaling or long-form writing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>Not something you need to do perfectly</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it works Section */}
      <section className="py-16 md:py-24 px-6 bg-[#F5F2ED]">
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-3xl md:text-4xl font-light text-[#2D2A26] text-center mb-16"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            The Reflect–Regulate–Redirect framework
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {/* Reflect */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-6">
                <Heart className="w-7 h-7 text-[#A87C6D]" />
              </div>
              <h3 
                className="text-2xl font-normal text-[#2D2A26] mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Reflect
              </h3>
              <p className="text-[#6E6A66] font-light leading-relaxed">
                Share what's present. Your feelings are named and mirrored without judgment.
              </p>
            </div>

            {/* Regulate */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#7A9E7E]/10 flex items-center justify-center mx-auto mb-6">
                <Wind className="w-7 h-7 text-[#7A9E7E]" />
              </div>
              <h3 
                className="text-2xl font-normal text-[#2D2A26] mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Regulate
              </h3>
              <p className="text-[#6E6A66] font-light leading-relaxed">
                A brief grounding practice to calm your nervous system. Just 2-5 minutes.
              </p>
            </div>

            {/* Redirect */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#7D98A1]/10 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-7 h-7 text-[#7D98A1]" />
              </div>
              <h3 
                className="text-2xl font-normal text-[#2D2A26] mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Redirect
              </h3>
              <p className="text-[#6E6A66] font-light leading-relaxed">
                One gentle suggestion — a ritual, a boundary, or permission to rest.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* A typical check-in */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="p-8 md:p-12 bg-[#F9F7F4] rounded-3xl text-center">
            <h3 
              className="text-2xl font-normal text-[#2D2A26] mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              A typical check-in looks like this
            </h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#A87C6D]/20 flex items-center justify-center text-[#A87C6D] font-medium">1</div>
                <span className="text-[#6E6A66] font-light">Choose your mood</span>
              </div>
              <span className="hidden md:block text-[#E6E2DD] text-2xl">→</span>
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#A87C6D]/20 flex items-center justify-center text-[#A87C6D] font-medium">2</div>
                <span className="text-[#6E6A66] font-light">Answer a gentle question</span>
              </div>
              <span className="hidden md:block text-[#E6E2DD] text-2xl">→</span>
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#A87C6D]/20 flex items-center justify-center text-[#A87C6D] font-medium">3</div>
                <span className="text-[#6E6A66] font-light">Receive a soft response</span>
              </div>
            </div>
            <p className="text-[#A8A4A0] text-sm mt-8 font-light">
              That's it. No homework. No pressure. Just a moment for yourself.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-6 bg-[#F5F2ED]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            className="text-3xl md:text-4xl font-light text-[#2D2A26] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Ready to begin?
          </h2>
          <p className="text-[#6E6A66] font-light mb-10 max-w-md mx-auto">
            Create your free account and take your first check-in whenever you're ready.
          </p>
          <Link to="/register">
            <Button 
              className="rounded-full px-10 py-6 text-lg bg-[#A87C6D] hover:bg-[#956d5f] text-white transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              Create your space
            </Button>
          </Link>
          <p className="text-[#A8A4A0] text-sm mt-6 font-light">
            Free to start. No credit card required.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
